import { Link } from "react-router-dom";
import { IMG, BRANDS, COMPANY } from "../data";
import WaitlistForm from "../components/WaitlistForm";
import Founding50Section from "../components/Founding50Section";
import FoundingBadge from "../components/FoundingBadge";

const scrollToWaitlist = (e) => {
  e.preventDefault();
  const el = document.getElementById("waitlist");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* ------------- HERO ------------- */}
      <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <div
          className="hero-image hero-drift"
          style={{ backgroundImage: `url(${IMG.heroMountain})` }}
        ></div>
        <div className="hero-overlay"></div>

        <div className="container-cu relative z-10 pb-20 pt-32 w-full">
          <div className="max-w-3xl">
            <p
              className="eyebrow eyebrow-light mb-6 fade-up"
              style={{ color: "var(--gold-soft)" }}
            >
              Casa Haya International · Northern Spain
            </p>
            <div className="fade-up mb-6">
              <FoundingBadge variant="dark" data-testid="hero-founding-badge" />
            </div>
            <h1
              className="heading-display on-hero fade-up delay-1 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mb-6"
              style={{ color: "var(--cream)" }}
            >
              Where the Mountains <br />
              <span className="heading-italic" style={{ color: "var(--gold)" }}>
                Meet Home
              </span>
            </h1>
            <p
              className="fade-up delay-2 text-lg sm:text-xl max-w-xl leading-relaxed mb-10"
              style={{ color: "rgba(250,247,242,0.88)" }}
            >
              Luxury short stays in the heart of Northern Spain — coming soon.
            </p>
            <div className="fade-up delay-3 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-register-cta"
                onClick={scrollToWaitlist}
                className="btn-cream"
              >
                Register Your Interest <i className="fa-solid fa-arrow-right text-sm"></i>
              </button>
              <Link
                data-testid="hero-brands-cta"
                to="/our-brands"
                className="text-sm uppercase tracking-[0.22em] link-underline"
                style={{ color: "var(--cream)" }}
              >
                Explore Our Brands
              </Link>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3 text-xs uppercase tracking-[0.22em]"
          style={{ color: "rgba(250,247,242,0.7)" }}
        >
          <span className="w-8 h-px" style={{ background: "var(--gold)" }}></span>
          Scroll to discover
        </div>
      </section>

      {/* ------------- ABOUT THE HOME ------------- */}
      <section className="section pattern-cream" data-testid="about-home-section">
        <div className="container-cu grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-5">The Apartment</p>
            <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl mb-8">
              Casa <br />
              <span className="heading-italic">Haya</span>
            </h2>
            <p className="text-base leading-relaxed mb-6" style={{ color: "var(--brown-soft)" }}>
              Nestled in the green landscapes of Northern Spain, Casa Haya
              is a fully furnished, modern retreat designed for guests who value
              comfort, calm and authenticity.
            </p>
            <p className="text-base leading-relaxed mb-8" style={{ color: "var(--brown-soft)" }}>
              Surrounded by mountains, forests and fresh air — it is the kind of
              place where you arrive as a guest and leave feeling like you were
              always home.
            </p>
            <span className="badge" data-testid="coming-2026-badge">
              <i className="fa-regular fa-calendar"></i> Coming 2026 · Limited availability
            </span>
            <div className="mt-10">
              <Link to="/the-apartment" className="btn-outline" data-testid="discover-apartment-cta">
                Discover the Apartment <i className="fa-solid fa-arrow-right text-sm"></i>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-8 aspect-[4/5] overflow-hidden rounded-md">
                <img
                  src={IMG.apartmentExterior}
                  alt="Modern home in Northern Spain"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                />
              </div>
              <div className="col-span-4 flex flex-col gap-4">
                <div className="aspect-square overflow-hidden rounded-md">
                  <img
                    src={IMG.interior1}
                    alt="Warm interior"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  />
                </div>
                <div className="aspect-square overflow-hidden rounded-md">
                  <img
                    src={IMG.greenValley}
                    alt="Green valley landscape"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------- WHY CASA HAYA ------------- */}
      <section className="section" style={{ background: "var(--cream-deep)" }} data-testid="why-section">
        <div className="container-cu">
          <div className="max-w-2xl mb-16">
            <p className="eyebrow mb-5">Why Casa Haya</p>
            <h2 className="heading-display text-4xl sm:text-5xl">
              A stay carved out of <span className="heading-italic">nature & craft</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                t: "Fully Furnished",
                d: "Everything you need from day one. Modern comfort in a natural setting.",
                i: "fa-couch",
              },
              {
                t: "Pure Nature",
                d: "Surrounded by the Picos de Europa, green valleys and mountain air.",
                i: "fa-mountain-sun",
              },
              {
                t: "Luxury Details",
                d: "Carefully curated interiors. Quality in every corner.",
                i: "fa-feather-pointed",
              },
            ].map((c, i) => (
              <div
                key={c.t}
                className="card-warm"
                data-testid={`why-card-${i}`}
              >
                <div
                  className="w-14 h-14 rounded-full inline-flex items-center justify-center mb-6"
                  style={{ background: "var(--cream)", color: "var(--forest)" }}
                >
                  <i className={`fa-solid ${c.i} text-lg`}></i>
                </div>
                <h3 className="heading-display text-2xl mb-3">{c.t}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--brown-soft)" }}>
                  {c.d}
                </p>
                <div className="mt-8 divider-gold opacity-60"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------- FOUNDING 50 ------------- */}
      <Founding50Section />

      {/* ------------- WAITLIST ------------- */}
      <section
        id="waitlist"
        className="section relative overflow-hidden"
        style={{ background: "var(--forest)" }}
        data-testid="waitlist-section"
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${IMG.mistyMountains})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "luminosity",
          }}
        ></div>
        <div className="container-narrow relative z-10">
          <div className="text-center mb-12">
            <div className="mb-5 flex justify-center">
              <FoundingBadge variant="dark" />
            </div>
            <h2
              className="heading-display text-4xl sm:text-5xl lg:text-6xl mb-6"
              style={{ color: "var(--cream)" }}
            >
              Be the First <span className="heading-italic" style={{ color: "var(--gold)" }}>to Know</span>
            </h2>
            <p
              className="text-base sm:text-lg max-w-xl mx-auto leading-relaxed"
              style={{ color: "rgba(250,247,242,0.82)" }}
            >
              Casa Haya opens for short stays in 2026. Register your
              interest and we'll reach out personally when availability opens.
            </p>
          </div>
          <div
            className="rounded-md p-8 sm:p-12"
            style={{
              background: "rgba(250,247,242,0.04)",
              border: "1px solid rgba(250,247,242,0.14)",
              backdropFilter: "blur(8px)",
            }}
          >
            <WaitlistForm source="homepage" variant="dark" />
          </div>
        </div>
      </section>

      {/* ------------- OUR PORTFOLIO ------------- */}
      <section className="section" data-testid="portfolio-section">
        <div className="container-cu">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow mb-5">Our Portfolio</p>
            <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl mb-6">
              Part of the <br />
              <span className="heading-italic">Casa Haya Family</span>.
            </h2>
            <p className="text-base leading-relaxed" style={{ color: "var(--brown-soft)" }}>
              Casa Haya International BV is the operational home of three
              AI-native brands — each built with the same care and intention as
              our physical spaces.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {BRANDS.map((b, i) => (
              <article
                key={b.id}
                className="brand-card"
                data-testid={`brand-card-${b.id}`}
              >
                <div className="accent-bar"></div>
                <div className="p-8 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    {b.logo ? (
                      <img
                        src={b.logo}
                        alt={`${b.name} logo`}
                        className="w-12 h-12 rounded-md object-cover"
                        style={{ background: b.logoBg, border: "1px solid var(--line)" }}
                      />
                    ) : (
                      <div
                        className="w-12 h-12 rounded-md inline-flex items-center justify-center"
                        style={{ background: "#C2185B", color: "#FFFFFF" }}
                      >
                        <i className="fa-solid fa-heart"></i>
                      </div>
                    )}
                    <span className={`badge ${b.statusType === "live" ? "badge-live" : "badge-soon"}`}>
                      {b.status}
                    </span>
                  </div>
                  <h3 className="heading-display text-3xl mb-2">{b.name}</h3>
                  <p
                    className="text-xs uppercase tracking-[0.22em] mb-5"
                    style={{ color: "var(--gold-deep)" }}
                  >
                    {b.tagline}
                  </p>
                  <p className="text-sm leading-relaxed mb-8 flex-1" style={{ color: "var(--brown-soft)" }}>
                    {b.short}
                  </p>
                  <a
                    data-testid={`brand-cta-${b.id}`}
                    href={b.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] link-underline font-medium"
                    style={{ color: "var(--forest)" }}
                  >
                    {b.cta} <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link data-testid="all-brands-cta" to="/our-brands" className="btn-outline">
              Explore Our Brands <i className="fa-solid fa-arrow-right text-sm"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ------------- FOUNDER ------------- */}
      <section
        className="section relative overflow-hidden"
        style={{ background: "var(--brown)" }}
        data-testid="founder-section"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${IMG.founderBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></div>
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(90deg, rgba(61,43,31,0.95) 0%, rgba(61,43,31,0.75) 60%, rgba(61,43,31,0.5) 100%)" }}
        ></div>

        <div className="container-cu relative z-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow eyebrow-light mb-5" style={{ color: "var(--gold-soft)" }}>
              Behind Casa Haya
            </p>
            <h2
              className="heading-display text-4xl sm:text-5xl lg:text-6xl mb-8"
              style={{ color: "var(--cream)" }}
            >
              A founder's <span className="heading-italic" style={{ color: "var(--gold)" }}>quiet conviction</span>.
            </h2>
            <p className="text-base leading-relaxed mb-5" style={{ color: "rgba(250,247,242,0.85)" }}>
              Casa Haya International BV was founded by{" "}
              <span style={{ color: "var(--gold-soft)" }}>{COMPANY.founder}</span> —
              entrepreneur, mother of three, married to a wonderful man since
              2008, and a firm believer in building things that last.
            </p>
            <p className="text-base leading-relaxed mb-10" style={{ color: "rgba(250,247,242,0.85)" }}>
              Operating from Northern Spain with Dutch roots, Ramona builds
              technology and spaces that serve people with warmth, intention and
              purpose. No team. No office. No excuses.
            </p>
          </div>

          <div className="lg:col-span-5">
            <blockquote
              className="relative pl-10 pr-4 py-6"
              style={{ borderLeft: "2px solid var(--gold)" }}
            >
              <i
                className="fa-solid fa-quote-left absolute -left-0 top-2 text-2xl"
                style={{ color: "var(--gold)", opacity: 0.5 }}
              ></i>
              <p
                className="heading-display text-3xl sm:text-4xl heading-italic leading-tight"
                style={{ color: "var(--cream)" }}
              >
                My different is my normal.
              </p>
              <p className="mt-4 text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gold-soft)" }}>
                — Ramona
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ------------- CONTACT STRIP ------------- */}
      <section className="section" data-testid="contact-strip">
        <div className="container-cu">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="eyebrow mb-5">Get in Touch</p>
              <h2 className="heading-display text-4xl sm:text-5xl mb-6">
                A conversation, <span className="heading-italic">always welcome.</span>
              </h2>
              <p className="text-base leading-relaxed max-w-md" style={{ color: "var(--brown-soft)" }}>
                Whether you're planning a stay, exploring our brands, or curious
                about partnership — we'd love to hear from you.
              </p>
            </div>
            <div
              className="card-warm"
              style={{ background: "var(--cream-deep)" }}
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="eyebrow mb-3">Email</p>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="font-serif text-xl link-underline"
                    style={{ color: "var(--forest)" }}
                  >
                    {COMPANY.email}
                  </a>
                </div>
                <div>
                  <p className="eyebrow mb-3">Address</p>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--brown-soft)" }}>
                    {COMPANY.address}
                  </p>
                </div>
                <div>
                  <p className="eyebrow mb-3">KvK</p>
                  <p className="text-sm" style={{ color: "var(--brown-soft)" }}>{COMPANY.kvk}</p>
                </div>
                <div>
                  <p className="eyebrow mb-3">BTW</p>
                  <p className="text-sm" style={{ color: "var(--brown-soft)" }}>{COMPANY.btw}</p>
                </div>
              </div>
              <div className="mt-8">
                <Link to="/contact" className="btn-primary" data-testid="contact-page-cta">
                  Visit Contact Page <i className="fa-solid fa-arrow-right text-sm"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
